import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, ValidationErrors } from '@angular/forms';
import {
  combineLatest,
  Observable,
  of,
  Subscription,
  distinctUntilChanged,
  defer,
  merge,
} from 'rxjs';
import { Store } from '@ngxs/store';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { LoadAllCompetenceRolesForCurrentDepartment } from 'src/app/competence/state/competence-roles.actions';
import { ICompetenceRoleSimple } from 'src/app/competence/model/competence-role.model';
import { CompetenceRolesState } from 'src/app/competence/state/competence-roles.state';
import { IOptionGroup } from '../../models/option-group.model';
import { IdName } from '../../models/id-name';
import { IsStringArrayPipe } from '../../pipes/is-string-array.pipe';
import { JobPositionState } from '../../../competence/state/job-position.state';
import { JobPositionGroupState } from '../../../competence/state/job-position-group.state';
import {
  LoadAllJobPositions,
  LoadAllJobPositionsForDepartmentAndGroups,
} from '../../../competence/state/job-position.actions';
import {
  LoadAllJobPositionGroups,
  LoadAllJobPositionGroupsForDepartmentAndGroups,
} from '../../../competence/state/job-position-group.actions';
import { UIElementsModule } from '../../ui-elements/ui-elements.module';

type MultipleSelectionValue = string[] | IdName[];

// TODO Illia: with roles it becomes not only job position and group selector so rename is in order
@Component({
  selector: 'app-job-position-and-group-multiselect',
  templateUrl: './job-position-and-group-multiselect.component.html',
  providers: [IsStringArrayPipe],
  imports: [UIElementsModule, AsyncPipe],
})
export class JobPositionAndGroupMultiselectComponent<P extends string, G extends string>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() form: FormGroup;
  @Input() positionFieldName: P;
  @Input() positionGroupFieldName: G;
  @Input() rolesFieldName: string;
  @Input() label: string;
  @Input() required = false;
  @Input() description: string;
  @Input() positionsOnlyForSelectedDep = false;
  @Input() isMultiple = true;
  @Input() skipDispatch = false;
  @Input() disabled = false;
  @Input() selectedDepartments: string[];
  @Input() selectedDepartmentGroups: string[];
  @Input() skipOptionsInit = false;
  @Input() optionGroups: IOptionGroup[] = [];
  @Input() selectAll = false;

  @Output() selectionChanged = new EventEmitter<void>();

  control = new FormControl<any>(this.isMultiple ? [] : '', { nonNullable: true });

  getAllJobPositions$: Observable<IdName[]> = this.store.select(
    JobPositionState.getAllJobPositions
  );
  getAllJobPositionGroups$: Observable<IdName[]> = this.store.select(
    JobPositionGroupState.getAllJobPositionGroups
  );

  getAllRoles$: Observable<ICompetenceRoleSimple[]> = this.store.select(
    CompetenceRolesState.getAllRolesForCurrentDepartment
  );

  getAllJobPositionsForDepartment$: Observable<IdName[] | undefined> = this.store.select(
    JobPositionState.getJobPositionsForDepartmentAndGroups
  );
  getAllJobPositionGroupsForDepartment$: Observable<IdName[] | undefined> = this.store.select(
    JobPositionGroupState.getJobPositionGroupsForDepartmentAndGroups
  );
  options$: Observable<IOptionGroup[]>;

  get formPositionControl(): FormControl<string | MultipleSelectionValue> | undefined {
    const control = this.form?.get(this.positionFieldName);
    return control
      ? (control as unknown as FormControl<string | MultipleSelectionValue>)
      : undefined;
  }

  get formPositionGroupControl(): FormControl<string | MultipleSelectionValue> | undefined {
    const control = this.form?.get(this.positionGroupFieldName);
    return control
      ? (control as unknown as FormControl<string | MultipleSelectionValue>)
      : undefined;
  }

  get formRolesControl(): FormControl<string | MultipleSelectionValue> | undefined {
    if (!this.rolesFieldName) {
      return undefined;
    }
    const control = this.form?.get(this.rolesFieldName);
    return control
      ? (control as unknown as FormControl<string | MultipleSelectionValue>)
      : undefined;
  }

  private firstOptionGroup: IdName[];
  private secondOptionGroup: IdName[];
  private thirdOptionGroup: IdName[];
  private subscriptions = new Subscription();
  private formSubscriptions = new Subscription();

  constructor(
    private store: Store,
    private isStringArrayPipe: IsStringArrayPipe,
    private cdr: ChangeDetectorRef
  ) {
    this.subscriptions.add(this.onControlValueChange());
  }

  ngOnInit(): void {
    if (this.rolesFieldName && !this.formRolesControl) {
      console.error(
        `JOB POSITION AND GROUP MULTISELECT [${this.label}]: rolesFieldName is set but such field does not exist in the form`
      );
    }

    this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.form?.currentValue) {
      this.formSubscriptions.unsubscribe();
      this.formSubscriptions = new Subscription();
      this.subscriptions.add(this.formSubscriptions);
      this.handleFormStatus(this.form.status);
      this.setInitialValuesOnControl();
      this.formSubscriptions.add(this.onFormStatusChange());
      this.formSubscriptions.add(this.onPositionFieldsValueChange());
    }
    if ((changes.required || changes.form) && this.required) {
      this.form.addValidators(this.validator);
    } else if (
      changes.required?.currentValue !== changes.required?.previousValue &&
      (this.form.validator || this.form.errors)
    ) {
      this.form.removeValidators(this.validator);
      this.form.setErrors(null);
      this.form.updateValueAndValidity();
    }
    if (
      changes.positionsOnlyForSelectedDep?.currentValue &&
      !changes.positionsOnlyForSelectedDep?.isFirstChange()
    ) {
      this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
    }
    if (
      changes.selectedDepartments?.currentValue ||
      changes.selectedDepartmentGroups?.currentValue
    ) {
      this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
    }
    if (changes.optionGroups?.currentValue) {
      this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.form) {
      this.form.removeValidators(this.validator);
      this.form.setErrors(null);
      this.form.updateValueAndValidity();
    }
  }

  onFormStatusChange(): Subscription {
    return this.form.statusChanges
      .pipe(
        distinctUntilChanged(),
        tap((status: FormControlStatus) => {
          this.handleFormStatus(status);
        }),
        tap(() => this.cdr.detectChanges())
      )
      .subscribe();
  }

  onControlValueChange(): Subscription {
    return this.control.valueChanges.subscribe((value) => {
      this.setItemsAndItemGroups(value);
      if (this.control.dirty) {
        this.form.markAsDirty();
      }
    });
  }

  onPositionFieldsValueChange(): Subscription {
    const observables: Observable<string | MultipleSelectionValue>[] = [];

    if (this.formPositionControl) {
      observables.push(this.formPositionControl.valueChanges);
    }
    if (this.formPositionGroupControl) {
      observables.push(this.formPositionGroupControl.valueChanges);
    }
    if (this.rolesFieldName && this.formRolesControl) {
      observables.push(this.formRolesControl.valueChanges);
    }

    if (observables.length === 0) {
      return new Subscription();
    }

    return merge(...observables).subscribe(() => {
      if (this.disabled || (this.formPositionControl && this.formPositionControl.disabled)) {
        this.control.disable({ emitEvent: false });
      } else {
        this.control.enable({ emitEvent: false });
      }
      this.control.setValue(this.formatControlValues(), { emitEvent: false });
    });
  }

  handleFormStatus(status?: FormControlStatus): void {
    if ((status && status === 'DISABLED') || this.disabled) {
      this.control.disable({ emitEvent: false });
    } else if (!this.disabled) {
      this.control.enable({ emitEvent: false });
    }
  }

  markAsTouched(): void {
    this.control.markAsTouched();
  }

  private setItemsAndItemGroups(value: string | string[]) {
    const values = Array.isArray(value) ? value : [value];
    const item = values.filter((id) => this.firstOptionGroup?.some((element) => element.id === id));
    const itemGroup = values.filter((id) =>
      this.secondOptionGroup?.some((elementGroup) => elementGroup.id === id)
    );
    const roles = values.filter((id) =>
      this.thirdOptionGroup?.some((element) => element.id === id)
    );

    if (this.formPositionControl) {
      const value = this.isMultiple ? item : item.length > 0 ? item[0] : '';
      this.formPositionControl.setValue(value as string | MultipleSelectionValue, {
        emitEvent: false,
      });
    }
    if (this.formPositionGroupControl) {
      const value = this.isMultiple ? itemGroup : itemGroup.length > 0 ? itemGroup[0] : '';
      this.formPositionGroupControl.setValue(value as string | MultipleSelectionValue, {
        emitEvent: false,
      });
    }
    if (this.rolesFieldName && this.formRolesControl) {
      const value = this.isMultiple ? roles : roles.length > 0 ? roles[0] : '';
      this.formRolesControl.setValue(value as string | MultipleSelectionValue, {
        emitEvent: false,
      });
    }

    this.selectionChanged.emit();
  }

  private initializeOptions(): Observable<IOptionGroup[]> {
    if (this.skipOptionsInit) {
      this.firstOptionGroup =
        this.optionGroups.find((group) => group.type === this.positionFieldName)?.options ?? [];
      this.secondOptionGroup =
        this.optionGroups.find((group) => group.type === this.positionGroupFieldName)?.options ??
        [];
      this.thirdOptionGroup =
        this.optionGroups.find((group) => group.type === this.rolesFieldName)?.options ?? [];
      return of(this.optionGroups).pipe(tap(() => this.setInitialValuesOnControl()));
    }
    const shouldLoadForSelectedDep =
      this.positionsOnlyForSelectedDep ||
      this.selectedDepartments?.length ||
      this.selectedDepartmentGroups?.length;

    if (!this.skipDispatch) {
      const actions: any[] = [];
      if (shouldLoadForSelectedDep) {
        actions.push(
          new LoadAllJobPositionsForDepartmentAndGroups(
            this.selectedDepartments,
            this.selectedDepartmentGroups
          ),
          new LoadAllJobPositionGroupsForDepartmentAndGroups(
            this.selectedDepartments,
            this.selectedDepartmentGroups
          )
        );
        if (this.rolesFieldName) {
          actions.push(new LoadAllCompetenceRolesForCurrentDepartment());
        }
      } else {
        actions.push(new LoadAllJobPositions(), new LoadAllJobPositionGroups());
        if (this.rolesFieldName) {
          actions.push(new LoadAllCompetenceRolesForCurrentDepartment());
        }
      }
      this.store.dispatch(actions);
    }
    const jobPositionsForAllDepartments$ = combineLatest([
      this.getAllJobPositions$,
      this.getAllJobPositionGroups$,
      this.getAllRoles$,
    ]);

    const jobPositionsForSelectedDepartment$ = combineLatest([
      this.getAllJobPositionsForDepartment$,
      this.getAllJobPositionGroupsForDepartment$,
      this.getAllRoles$,
    ]);

    return defer(() =>
      shouldLoadForSelectedDep ? jobPositionsForSelectedDepartment$ : jobPositionsForAllDepartments$
    ).pipe(
      switchMap(([elements, elementGroups, roles]) => {
        this.firstOptionGroup = elements ?? [];
        this.secondOptionGroup = elementGroups ?? [];
        this.thirdOptionGroup = roles ?? [];

        const optionGroups: IOptionGroup[] = [
          {
            type: this.positionFieldName,
            options: this.firstOptionGroup,
          },
          {
            type: this.positionGroupFieldName,
            options: this.secondOptionGroup,
          },
        ];

        if (this.rolesFieldName) {
          optionGroups.push({
            type: this.rolesFieldName,
            options: this.thirdOptionGroup,
          });
        }

        return of(optionGroups);
      }),
      tap(() => this.setInitialValuesOnControl())
    );
  }

  private setInitialValuesOnControl(): void {
    this.control.setValue(this.formatControlValues(), { emitEvent: false });
  }

  private formatControlValues(): string[] | MultipleSelectionValue | string {
    if (!this.isMultiple) {
      const positionValue = this.formPositionControl?.value;
      const positionGroupValue = this.formPositionGroupControl?.value;
      const rolesValue =
        this.rolesFieldName && this.formRolesControl ? this.formRolesControl.value : '';

      return positionValue || positionGroupValue || rolesValue || '';
    }

    const chosenItemsAndGroups: string[] = [];
    if (this.formPositionControl && this.formPositionControl.value !== null) {
      const value = this.formPositionControl.value as MultipleSelectionValue;
      if (this.isStringArray(value)) {
        value.forEach((id) => chosenItemsAndGroups.push(id));
      } else {
        value.forEach(({ id }) => chosenItemsAndGroups.push(id));
      }
    }
    if (this.formPositionGroupControl && this.formPositionGroupControl.value !== null) {
      const value = this.formPositionGroupControl.value as MultipleSelectionValue;
      if (this.isStringArray(value)) {
        value.forEach((id) => chosenItemsAndGroups.push(id));
      } else {
        value.forEach(({ id }) => chosenItemsAndGroups.push(id));
      }
    }

    if (this.rolesFieldName && this.formRolesControl && this.formRolesControl.value !== null) {
      const value = this.formRolesControl.value as MultipleSelectionValue;
      if (this.isStringArray(value)) {
        value.forEach((id) => chosenItemsAndGroups.push(id));
      } else {
        value.forEach(({ id }) => chosenItemsAndGroups.push(id));
      }
    }
    return chosenItemsAndGroups;
  }

  private isStringArray(array: MultipleSelectionValue): array is string[] {
    return this.isStringArrayPipe.transform(array);
  }

  private isNotNull<T>(value: T): value is NonNullable<T> {
    return value !== null && value !== undefined;
  }

  private validator = (): ValidationErrors | null => {
    const positionExists = this.formPositionControl?.value?.length;
    const positionGroupExists = this.formPositionGroupControl?.value?.length;
    const rolesExists =
      this.rolesFieldName && this.formRolesControl ? this.formRolesControl.value?.length : true;

    return !positionExists && !positionGroupExists && !rolesExists
      ? { jobPositionOrJobPositionGroupRequired: true }
      : null;
  };
}
